<template>
    <!-- @click.stop: prevent openDetail on multi-asset-cell -->
    <div class="ma-media-gallery-overlay" @click.self.stop="handleClose">
        <div class="ma-gallery-container" @click.stop>
            <div class="ma-gallery-header">
                <div class="ma-gallery-file-info">
                    <span class="ma-gallery-file-name">{{ currentAsset.name || $t("tables.mediaGalleryUntitled") }}</span>
                    <span class="ma-gallery-counter" v-if="assets.length > 1">
                        {{ currentIndex + 1 }} / {{ assets.length }}
                    </span>
                </div>
                <div class="ma-gallery-header-actions">
                    <a-tooltip :title="$t('table.downloadFile')">
                        <a-button type="link" class="ma-gallery-action-btn" @click.stop="handleDownload">
                            <a-icon type="download" />
                        </a-button>
                    </a-tooltip>
                    <a-tooltip :title="$t('table.openInNewTab')">
                        <a-button type="link" class="ma-gallery-action-btn" @click.stop="handleOpenInNewTab">
                            <a-icon type="export" />
                        </a-button>
                    </a-tooltip>
                    <a-button type="link" class="ma-gallery-action-btn ma-gallery-close-btn" @click.stop="handleClose">
                        <a-icon type="close" />
                    </a-button>
                </div>
            </div>

            <button
                type="button"
                class="ma-gallery-nav ma-gallery-nav-prev"
                v-if="assets.length > 1"
                @click.stop="goToPrev"
                :disabled="currentIndex === 0"
                :aria-label="$t('table.mediaGalleryPrevious')"
            >
                <a-icon type="left" />
            </button>
            <button
                type="button"
                class="ma-gallery-nav ma-gallery-nav-next"
                v-if="assets.length > 1"
                @click.stop="goToNext"
                :disabled="currentIndex === assets.length - 1"
                :aria-label="$t('table.mediaGalleryNext')"
            >
                <a-icon type="right" />
            </button>

            <div class="ma-gallery-body">
                <div class="ma-gallery-image-wrap" v-if="variant === 'image' && currentUrl">
                    <img :src="currentUrl" :alt="currentAsset.name || ''" />
                </div>

                <div class="ma-gallery-video-wrap" v-else-if="variant === 'video' && currentUrl">
                    <video
                        :key="mediaElementKey"
                        class="ma-gallery-video"
                        :src="currentUrl"
                        controls
                        playsinline
                    ></video>
                </div>

                <div class="ma-gallery-audio-wrap" v-else-if="variant === 'audio'">
                    <div class="ma-gallery-audio-inner" v-if="currentUrl">
                        <a-icon type="sound" class="ma-gallery-audio-deco" />
                        <div class="ma-gallery-audio-play-row">
                            <a-button
                                type="primary"
                                shape="circle"
                                size="large"
                                class="ma-gallery-audio-play-btn"
                                @click.stop="toggleGalleryAudio"
                            >
                                <a-icon :type="audioPlaying ? 'pause' : 'caret-right'" />
                            </a-button>
                        </div>
                        <audio
                            ref="galleryAudio"
                            :key="mediaElementKey"
                            class="ma-gallery-audio-el"
                            :src="currentUrl"
                            controls
                            preload="auto"
                            playsinline
                            @play="onGalleryAudioPlay"
                            @pause="onGalleryAudioPause"
                            @ended="onGalleryAudioEnded"
                        ></audio>
                    </div>
                    <div class="ma-gallery-audio-missing" v-else>
                        <a-icon type="exclamation-circle" />
                        <p>{{ $t("table.audioSourceMissing") }}</p>
                    </div>
                </div>

                <div class="ma-gallery-file-wrap" v-else-if="variant === 'file'">
                    <div class="ma-gallery-file-panel">
                        <a-icon :type="antFileIcon(currentAsset)" class="ma-gallery-file-bigicon" />
                        <h3 class="ma-gallery-file-title">{{ currentAsset.name || "—" }}</h3>
                        <div class="ma-gallery-file-actions">
                            <a-button type="primary" @click.stop="handleDownload">
                                <a-icon type="download" />
                                {{ $t("table.downloadFile") }}
                            </a-button>
                            <a-button @click.stop="handleOpenInNewTab">
                                <a-icon type="export" />
                                {{ $t("table.openInNewTab") }}
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="ma-gallery-thumbs" v-if="assets.length > 1">
                <div
                    class="ma-gallery-thumb"
                    :class="{ active: index === currentIndex }"
                    v-for="(asset, index) in assets"
                    :key="asset._id || index"
                    @click.stop="goToIndex(index)"
                >
                    <img v-if="variant === 'image' && thumbUrl(asset)" :src="thumbUrl(asset)" :alt="asset.name || ''" />
                    <template v-else-if="variant === 'video'">
                        <a-icon type="video-camera" class="ma-gallery-thumb-icon" />
                    </template>
                    <template v-else-if="variant === 'audio'">
                        <a-icon type="sound" class="ma-gallery-thumb-icon" />
                    </template>
                    <template v-else>
                        <a-icon :type="antFileIcon(asset)" class="ma-gallery-thumb-icon" />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "MultiAssetMediaPreviewOverlay",
    props: {
        variant: {
            type: String,
            required: true,
            validator: function (v) {
                return ["image", "video", "audio", "file"].indexOf(v) !== -1;
            },
        },
        assets: {
            type: Array,
            default: function () {
                return [];
            },
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
    },
    data: function () {
        return {
            currentIndex: 0,
            audioPlaying: false,
        };
    },
    computed: {
        currentAsset: function () {
            return this.assets[this.currentIndex] || {};
        },
        currentUrl: function () {
            return this.assetUrl(this.currentAsset);
        },
        mediaElementKey: function () {
            var a = this.currentAsset;
            if (!a || !a._id) return String(this.currentIndex);
            return String(a._id) + "-" + String(this.currentIndex);
        },
    },
    watch: {
        initialIndex: function (val) {
            this.currentIndex = val;
        },
        currentIndex: function () {
            this.audioPlaying = false;
            this.pauseDetachedMedia();
        },
    },
    mounted: function () {
        this.currentIndex = this.initialIndex;
        document.addEventListener("keydown", this.handleKeydown, true);
        document.body.style.overflow = "hidden";
    },
    beforeDestroy: function () {
        this.pauseDetachedMedia();
        document.removeEventListener("keydown", this.handleKeydown, true);
        document.body.style.overflow = "";
    },
    methods: {
        assetUrl: function (asset) {
            if (!asset) return "";
            if (asset.cdnUrl) return asset.cdnUrl;
            return "https://static.supsis.live/" + asset.path + asset._id + "." + asset.ext;
        },
        thumbUrl: function (asset) {
            return this.assetUrl(asset);
        },
        antFileIcon: function (asset) {
            var mime = (asset && asset.mime) ? asset.mime : "";
            if (mime.indexOf("image") !== -1) return "file-image";
            if (mime.indexOf("video") !== -1) return "video-camera";
            if (mime.indexOf("audio") !== -1) return "sound";
            if (mime.indexOf("pdf") !== -1) return "file-pdf";
            if (mime.indexOf("spreadsheet") !== -1 || mime.indexOf("excel") !== -1) return "file-excel";
            if (mime.indexOf("word") !== -1 || mime.indexOf("document") !== -1) return "file-word";
            var ext = asset && asset.ext ? String(asset.ext).toLowerCase() : "";
            if (ext === "pdf") return "file-pdf";
            if (ext === "xls" || ext === "xlsx") return "file-excel";
            if (ext === "doc" || ext === "docx") return "file-word";
            if (ext === "zip" || ext === "rar") return "file-zip";
            return "file";
        },
        toggleGalleryAudio: function () {
            var self = this;
            this.$nextTick(function () {
                var el = self.$refs.galleryAudio;
                if (Array.isArray(el)) {
                    el = el[0];
                }
                if (!el || !self.currentUrl) {
                    return;
                }
                if (el.paused) {
                    el.play().catch(function () {});
                } else {
                    el.pause();
                }
            });
        },
        onGalleryAudioPlay: function () {
            this.audioPlaying = true;
        },
        onGalleryAudioPause: function () {
            this.audioPlaying = false;
        },
        onGalleryAudioEnded: function () {
            this.audioPlaying = false;
        },
        pauseDetachedMedia: function () {
            var root = this.$el;
            if (!root) return;
            var videos = root.querySelectorAll("video");
            for (var i = 0; i < videos.length; i++) {
                try {
                    videos[i].pause();
                } catch (e) {}
            }
            var audios = root.querySelectorAll("audio");
            for (var j = 0; j < audios.length; j++) {
                try {
                    audios[j].pause();
                } catch (e2) {}
            }
        },
        handleClose: function () {
            this.$emit("close");
        },
        goToPrev: function () {
            if (this.currentIndex > 0) {
                this.currentIndex--;
            }
        },
        goToNext: function () {
            if (this.currentIndex < this.assets.length - 1) {
                this.currentIndex++;
            }
        },
        goToIndex: function (index) {
            if (index >= 0 && index < this.assets.length) {
                this.currentIndex = index;
            }
        },
        handleDownload: function () {
            var url = this.currentUrl;
            if (!url) return;
            var link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            link.download = this.currentAsset.name || "file";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        handleOpenInNewTab: function () {
            var url = this.currentUrl;
            if (!url) return;
            window.open(url, "_blank");
        },
        handleKeydown: function (event) {
            if (event.key === "Escape") {
                event.stopPropagation();
                event.preventDefault();
                this.handleClose();
            } else if (event.key === "ArrowLeft") {
                event.stopPropagation();
                this.goToPrev();
            } else if (event.key === "ArrowRight") {
                event.stopPropagation();
                this.goToNext();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.ma-media-gallery-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.92);
    z-index: 10050;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: maMediaGalleryFadeIn 0.2s ease;
}

@keyframes maMediaGalleryFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.ma-gallery-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.ma-gallery-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    z-index: 10;
    flex-shrink: 0;

    .ma-gallery-file-info {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    .ma-gallery-file-name {
        font-size: 16px;
        font-weight: 500;
        max-width: min(400px, 50vw);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .ma-gallery-counter {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
        background: rgba(255, 255, 255, 0.1);
        padding: 4px 10px;
        border-radius: 12px;
        flex-shrink: 0;
    }

    .ma-gallery-header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ma-gallery-action-btn {
        color: #fff;
        font-size: 18px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s;

        &:hover {
            background: rgba(255, 255, 255, 0.1);
        }
    }

    .ma-gallery-close-btn {
        font-size: 20px;
    }
}

.ma-gallery-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    &.ma-gallery-nav-prev {
        left: 20px;
    }

    &.ma-gallery-nav-next {
        right: 20px;
    }
}

.ma-gallery-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 16px 80px;
    min-height: 0;
}

.ma-gallery-image-wrap {
    max-width: 100%;
    max-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        max-width: 100%;
        max-height: calc(100vh - 200px);
        object-fit: contain;
        border-radius: 4px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
}

.ma-gallery-video-wrap {
    width: 100%;
    max-width: 1100px;
    max-height: calc(100vh - 200px);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ma-gallery-video {
    width: 100%;
    max-height: calc(100vh - 200px);
    border-radius: 8px;
    background: #000;
}

.ma-gallery-audio-wrap {
    width: 100%;
    max-width: 560px;
    padding: 24px;
}

.ma-gallery-audio-inner {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 28px 24px 20px;
    text-align: center;
}

.ma-gallery-audio-deco {
    font-size: 56px;
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 16px;
}

.ma-gallery-audio-play-row {
    margin-bottom: 20px;
}

.ma-gallery-audio-play-btn {
    width: 64px;
    height: 64px;
    font-size: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.ma-gallery-audio-el {
    width: 100%;
    min-width: 280px;
    min-height: 54px;
    display: block;
    margin: 0 auto;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.95);
}

.ma-gallery-audio-missing {
    text-align: center;
    color: rgba(255, 255, 255, 0.75);
    padding: 40px 20px;

    .anticon {
        font-size: 48px;
        margin-bottom: 12px;
    }

    p {
        margin: 0;
        font-size: 14px;
    }
}

.ma-gallery-file-wrap {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.ma-gallery-file-panel {
    background: var(--color-bg-default, #fff);
    border-radius: 12px;
    padding: 40px 32px;
    text-align: center;
    max-width: 420px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
}

.ma-gallery-file-bigicon {
    font-size: 72px;
    color: #5f6368;
    margin-bottom: 16px;
}

.ma-gallery-file-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 24px;
    word-break: break-word;
    color: var(--color-text-primary, #333);
}

.ma-gallery-file-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
}

.ma-gallery-thumbs {
    display: flex;
    gap: 8px;
    padding: 12px 20px;
    background: rgba(0, 0, 0, 0.5);
    overflow-x: auto;
    justify-content: center;
    flex-shrink: 0;
    flex-wrap: nowrap;

    .ma-gallery-thumb {
        width: 60px;
        height: 60px;
        flex-shrink: 0;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        background: rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;

        &:hover {
            border-color: rgba(255, 255, 255, 0.5);
        }

        &.active {
            border-color: #25d366;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .ma-gallery-thumb-icon {
            font-size: 28px;
            color: rgba(255, 255, 255, 0.85);
        }
    }
}
</style>
